import { __rest } from 'tslib';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import MuiBox from '@mui/material/Box';
import MuiDivider from '@mui/material/Divider';
import MuiTypography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import React__default, { useMemo, useCallback } from 'react';
import { components } from 'react-select';
import { ReactComponent as ForwardRef } from '../../../assets/icons/CheckSmall.svg.js';
import useHighlight from '../../../hooks/useHighlight.js';
import Hint from '../../Hint/Hint.js';
import { useDropdown } from '../context/DropdownProvider.js';

const CheckedIcon = styled(ForwardRef, { shouldForwardProp: prop => !['selected', 'size'].includes(prop) }) `
  fill: transparent;
  opacity: ${({ selected }) => (selected ? 1 : 0)};
  margin: ${({ theme, size }) => (size === 'large' ? theme.spacing(1, 2, 1, 0) : theme.spacing(0, 1, 0, 0))};
`;
// eslint-disable-next-line react/display-name
const BaseText = React__default.forwardRef((props, ref) => (jsx(MuiTypography, Object.assign({ component: "span", ref: ref }, props))));
const Text = styled(BaseText) `
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;
  white-space: nowrap;
  flex: 1;
  .highlighted-text {
    color: ${({ theme }) => theme.palette.common.white};
    background-color: transparent;
  }
`;
const ActionsContainer = styled(MuiBox, { shouldForwardProp: prop => !['size'].includes(prop) }) `
  display: flex;
  flex-direction: row;
  margin: ${({ theme, size }) => theme.spacing(0, size === 'large' ? 2 : 1)};
  color: ${({ theme }) => theme.palette.N10.main};
  > :not(:last-child) {
    margin: ${({ theme }) => theme.spacing(0, 1, 0, 0)};
  }
  > :hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;
const ActionIconContainer = styled(MuiBox) `
  display: flex;
  padding: ${({ theme }) => theme.spacing(0.5)};
`;
const disabledClickStyle = { pointerEvents: 'none' };
const OptionItem = (_a) => {
    var _b;
    var { data, isSelected, innerProps: { onClick: innerOnClick }, clearValue, selectProps, isDisabled } = _a, rest = __rest(_a, ["data", "isSelected", "innerProps", "clearValue", "selectProps", "isDisabled"]);
    const { label, component: LabelComponent, actions, hint, hintDuration, dividerTop, dividerBottom, onClick, isNone, shouldCloseMenuOnSelect = true, } = data;
    const { size, disableHighlighting } = useDropdown();
    const markText = useHighlight((_b = selectProps.inputValue) === null || _b === void 0 ? void 0 : _b.trim());
    const optionLabel = useMemo(() => {
        if (isDisabled || disableHighlighting) {
            return label;
        }
        return markText(label);
    }, [isDisabled, markText, label, disableHighlighting]);
    const onItemClick = useCallback(e => {
        var _a, _b;
        if (isNone) {
            clearValue();
            (_a = selectProps.onMenuClose) === null || _a === void 0 ? void 0 : _a.call(selectProps);
        }
        else if (onClick) {
            onClick();
            (_b = selectProps.onMenuClose) === null || _b === void 0 ? void 0 : _b.call(selectProps);
        }
        else {
            innerOnClick === null || innerOnClick === void 0 ? void 0 : innerOnClick(e);
        }
    }, [innerOnClick, isNone, onClick, clearValue, selectProps]);
    const optionProps = useMemo(() => {
        return { 'aria-label': 'option-item', onClick: onItemClick };
    }, [onItemClick]);
    return (jsxs(Fragment, { children: [dividerTop && jsx(MuiDivider, {}), jsxs(components.Option, Object.assign({ data: data, isSelected: isSelected, clearValue: clearValue, selectProps: selectProps, innerProps: optionProps, isDisabled: isDisabled }, rest, { children: [!isNone && !onClick && jsx(CheckedIcon, { size: size, selected: isSelected }), jsx(Hint, Object.assign({ title: hint, duration: hintDuration !== null && hintDuration !== void 0 ? hintDuration : 'fast' }, { children: jsx(Text, Object.assign({ variant: size === 'large' ? 'subtitle1' : 'body1', style: !shouldCloseMenuOnSelect ? disabledClickStyle : undefined }, { children: LabelComponent ? jsx(LabelComponent, { label: optionLabel }) : optionLabel })) })), !!actions && (jsx(ActionsContainer, Object.assign({ size: size, id: "actions" }, { children: actions.map(({ icon: Icon, name, onClick }) => (jsx(ActionIconContainer, Object.assign({ "data-testid": `actions-${name}`, onClick: e => {
                                e.stopPropagation();
                                onClick(data, name, selectProps.onMenuClose);
                            } }, { children: jsx(Icon, { style: disabledClickStyle }) }), name))) })))] })), dividerBottom && jsx(MuiDivider, { id: "bottomDivider" })] }));
};

export { OptionItem as default };
//# sourceMappingURL=OptionItem.js.map
